import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import cx from 'classnames';
import { Switch, Route, Redirect } from 'react-router-dom';
// creates a beautiful scrollbar
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

// core components
import AdminNavbar from 'components/Navbars/AdminNavbar';
import Sidebar from 'components/Sidebar/Sidebar';
import DialogManager from 'components/DialogManager/DialogManager';
import FullScreenLoading from 'components/FullScreenLoading/FullScreenLoading';

import routes from 'routes';
import { filterRoutesByUserRole } from 'plugins/router/guards';
import layoutNames from 'utils/enums/layout-names.enum';

import styles from 'assets/jss/material-dashboard-pro-react/layouts/adminStyle';

import image from 'assets/img/sidebar-2.jpg';
import logo from 'assets/img/sevis-logo.svg';

import AuthService from 'services/auth.service';
import { getPortalSettings } from 'services/portal-settings.service';
import { getLicenseSettings } from 'services/license.service';
import { setAlertActionCreator, setExpandSidebarActionCreator } from 'plugins/store/reducers/common.reducer';
import { setAutoLockSettingsActionCreator } from 'plugins/store/reducers/auth.reducer';
import {
	setPortalSettingsActionCreator,
} from 'plugins/store/reducers/settings.reducer';

import { mapSettingsToObject } from 'mappings/portalSettings.mapping';
import { canByLicensing } from 'utils/licensing/licensingChecker';

import useTaskResolver from 'hooks/useTaskResolver';

var ps;

let warnTimeout = null;
let lockTimeout = null;

const clearLockTimeout = () => {
	if (warnTimeout) clearTimeout(warnTimeout);
	if (lockTimeout) clearTimeout(lockTimeout);
};

let autoLockState = {};

const useStyles = makeStyles(styles);

const AdminLayout = (props) => {
	const {
		autoLock,
		setAlert,
		setPortalSettings,
		setAutoLockSettings,
		isExpandedSidebar,
		toggleSidebar,
	} = props;

	useTaskResolver();
	const { loading, alert, pageHeaderText, ...rest } = props;
	// states and functions
	const [mobileOpen, setMobileOpen] = React.useState(false);
	let localStorageTriggered = false;
	// const [hasImage, setHasImage] = React.useState(true);
	// styles
	const color = 'red';
	const bgColor = 'black';
	const classes = useStyles();
	const mainPanelClasses =
		classes.mainPanel +
		' ' +
		cx({
			[classes.mainPanelSidebarMini]: !isExpandedSidebar,
			[classes.mainPanelWithPerfectScrollbar]:
				navigator.platform.indexOf('Win') > -1,
		});
	// ref for main panel div
	const mainPanel = React.createRef();

	const events = [
		'load',
		'mousemove',
		'mousedown',
		'click',
		'scroll',
		'keypress',
	];

	let resetTimeoutFromAnotherTab;

	// effect instead of componentDidMount,
	React.useEffect(() => {
		for (var i in events) {
			window.addEventListener(events[i], resetTimeout);
		}

		// eslint-disable-next-line
		resetTimeoutFromAnotherTab = () => {
			resetTimeout({ fromAnotherTab: true });
		};
		window.addEventListener('storage', resetTimeoutFromAnotherTab);

		refreshPortalSettings(resetTimeout);
		getLicenseSettings();
		return function cleanup() {
			destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		autoLockState = autoLock;
		resetTimeout();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoLock]);

	// effect instead of componentDidUpdate and componentWillUnmount
	React.useEffect(() => {
		if (navigator.platform.indexOf('Win') > -1) {
			ps = new PerfectScrollbar(mainPanel.current, {
				suppressScrollX: true,
				suppressScrollY: false,
			});
			document.body.style.overflow = 'hidden';
		}
		window.addEventListener('resize', resizeFunction);

		// Specify how to clean up after this effect:
		return function cleanup() {
			if (navigator.platform.indexOf('Win') > -1) {
				ps.destroy();
			}
			window.removeEventListener('resize', resizeFunction);
		};
	});

	// functions for changeing the states from components
	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};
	const getRoute = () => {
		return window.location.pathname !== `/${layoutNames.portal}/full-screen-maps`;
	};

	// eslint-disable-next-line no-unused-vars
	const getActiveRoute = (routes, parentMatch = null) => {
		let activeRoute = 'Default Brand Text';
		for (let i = 0; i < routes.length; i++) {
			if (routes[i].collapse || routes[i].views?.length) {
				let collapseActiveRoute = getActiveRoute(
					routes[i].views,
					routes[i].path
				);
				if (collapseActiveRoute !== activeRoute) {
					return collapseActiveRoute;
				}
			} else {
				const combinedPath = `${routes[i].layout}${parentMatch || ''}${routes[i].path
					}`
					.split('/')
					.filter((segment) => segment.charAt(0) !== ':')
					.join('/');

				const matched =
					routes[i].path === ''
						? window.location.pathname === combinedPath
						: window.location.pathname.indexOf(combinedPath) !== -1;

				if (matched) {
					return routes[i].name;
				}
			}
		}
		return activeRoute;
	};
	const getRoutes = (routes, parentMatch = null) => {
		return routes.filter(filterRoutesByUserRole).map((prop, key) => {
			if (!canByLicensing(prop.licensesFeatureName)) {
				return null;
			}
			if (prop.collapse || prop.views?.length) {
				return getRoutes(prop.views, prop.path);
			}
			if (prop.layout === `/${layoutNames.portal}`) {
				const combinedPath = `${prop.layout}${parentMatch || ''}${prop.path
					}`;

				return (
					<Route
						exact={prop.path === ''}
						path={combinedPath}
						component={prop.component}
						key={key}
					/>
				);
			} else {
				return null;
			}
		});
	};
	const sidebarMinimize = () => {
		toggleSidebar(!isExpandedSidebar);
	};
	const goBack = () => {
		props.history.go(-1);
	};
	const resizeFunction = () => {
		if (window.innerWidth >= 960) {
			setMobileOpen(false);
		}
	};

	const refreshPortalSettings = async (callback = () => { }) => {
		try {
			const settings = await getPortalSettings();
			const portalSettings = mapSettingsToObject(settings);

			setPortalSettings(portalSettings || {});

			if (portalSettings.autoLogoutNotificationTimeout && portalSettings.autoLogoutTimeout) {

				setAutoLockSettings({
					warningTimeout: portalSettings.autoLogoutNotificationTimeout,
					lockTimeout: portalSettings.autoLogoutTimeout,
				});
			}
		} catch (error) {
			console.log(error);
		} finally {
			callback();
		}
	};


	const setLockScreenTimeout = () => {
		warnTimeout = setTimeout(warn, autoLockState.warningTimeout * 60000);
		lockTimeout = setTimeout(lock, (autoLockState.warningTimeout + autoLockState.lockTimeout) * 60000);
	};

	const triggerLocalStorageForResetTimeout = () => {
		if (!localStorageTriggered) {
			localStorageTriggered = true;
			localStorage.setItem('resetTimeout', Date.now());
			setTimeout(() => {
				localStorageTriggered = false;
			}, autoLockState.warningTimeout * 60000 * 0.5);
		}
	};

	const resetTimeout = ({ fromAnotherTab } = {}) => {
		if (!fromAnotherTab) {
			triggerLocalStorageForResetTimeout();
		}
		clearLockTimeout();
		setLockScreenTimeout();
	};

	const warn = () => {
		setAlert({
			type: 'info',
			title: 'Auto Lock Warning',
			message:
				"We didn't discover any activity from you for a while. You session will be closed soon",
			confirmBtnText: 'Keep Alive',
			onConfirm: () => {
				setAlert(null);
			},
		});
	};

	const lock = () => {
		destroy();
		AuthService.logout();
		props.history.push(`/${layoutNames.auth}/login`);
	};

	const destroy = () => {
		clearLockTimeout();
		setAlert(null);

		for (var i in events) {
			window.removeEventListener(events[i], resetTimeout);
		}
		window.removeEventListener('storage', resetTimeoutFromAnotherTab);
	};

	return (
		<div className={classes.wrapper}>
			{loading && <FullScreenLoading />}
			<Sidebar
				routes={routes}
				logoTextLine1={'eCallMe!'}
				logoTextLine2={'Customer Engagement Solutions'}
				logo={logo}
				image={image}
				handleDrawerToggle={handleDrawerToggle}
				open={mobileOpen}
				color={color}
				bgColor={bgColor}
				miniActive={!isExpandedSidebar}
				{...rest}
			/>
			<div className={`with-perfect-scroll-bar ${mainPanelClasses}`} ref={mainPanel}>
				<AdminNavbar
					sidebarMinimize={sidebarMinimize.bind(this)}
					miniActive={!isExpandedSidebar}
					brandText={pageHeaderText || ''}
					handleDrawerToggle={handleDrawerToggle}
					goBack={goBack}
					{...rest}
				/>
				{/* On the /maps/full-screen-maps route we want the map to be on full screen - this is not possible if the content and container classes are present because they have some paddings which would make the map smaller */}
				{getRoute() ? (
					<div className={classes.content}>
						<div className={classes.container}>
							<Switch>
								{getRoutes(routes)}
								<Redirect from={`/${layoutNames.portal}`} to="/home" />
							</Switch>
						</div>
					</div>
				) : (
						<div className={classes.map}>
							<Switch>
								{getRoutes(routes)}
								<Redirect from={`/${layoutNames.portal}`} to="/home" />
							</Switch>
						</div>
					)}
			</div>
			{alert && (
				<DialogManager
					type={alert.type}
					title={alert.title}
					message={alert.message}
					children={alert.children}
					disabled={alert.disabled}
					onConfirm={alert.onConfirm}
					onCancel={alert.onCancel}
					confirmBtnText={alert.confirmBtnText}
					cancelBtnText={alert.cancelBtnText}
					showIcon={alert.showIcon}
					maxWidth={alert.maxWidth}
					confirmBtnColor={alert.confirmBtnColor}
					cancelBtnColor={alert.cancelBtnColor}
				/>
			)}
		</div>
	);
};

const mapStateToProps = ({
	common: {
		loading,
		alert,
		pageHeaderText,
		isExpandedSidebar,
	},
	auth: { autoLock },
	...props
}) => {
	return {
		...props,
		autoLock,
		loading,
		alert,
		pageHeaderText,
		isExpandedSidebar,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setPortalSettings: bindActionCreators(
		setPortalSettingsActionCreator,
		dispatch
	),
	setAutoLockSettings: bindActionCreators(
		setAutoLockSettingsActionCreator,
		dispatch
	),
	toggleSidebar: bindActionCreators(
		setExpandSidebarActionCreator,
		dispatch,
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminLayout);
